'use client';

import { useMemo, useCallback, Fragment } from 'react';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { cn } from '@/lib/utils';
import type { Team, Tournament } from '@/lib/types/auction';
import { teamService } from '@/services/api/teams';
import { useRouter } from 'next/navigation';
import { Eye, Trash, Edit, MoreVertical } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface TeamActionsProps {
  team: Team;
  tournament?: Tournament;
  onAction?: () => void;
  onEdit?: (team: Team) => void;
}

interface MenuItemProps {
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  className?: string;
}

const MenuItem = ({
  onClick,
  icon: Icon,
  label,
  className = 'text-gray-700',
}: MenuItemProps) => (
  <button
    onClick={onClick}
    className={cn(
      'flex w-full items-center px-4 py-2 text-left text-sm hover:bg-gray-100',
      className
    )}
  >
    <Icon className="mr-3 h-4 w-4" />
    {label}
  </button>
);

export function TeamActions({
  team,
  tournament,
  onAction,
  onEdit,
}: TeamActionsProps) {
  const router = useRouter();
  const { user } = useAuth();

  const canManageTeam = useMemo(() => {
    if (!team) return false;
    const isTeamOwner = team?.owner?.id === user?.id;
    const tournamentOwner = tournament?.owner?.id || team.tournament?.owner?.id;
    const isTournamentOwner = tournamentOwner === user?.id;
    return isTeamOwner || isTournamentOwner;
  }, [team, tournament, user]);

  const handleDelete = useCallback(async () => {
    if (!team) return;
    try {
      await teamService.delete(team.id);
      if (onAction) {
        onAction();
      } else {
        router.refresh();
      }
      router.push('/dashboard/teams');
    } catch (error) {
      console.error('Failed to delete team:', error);
    }
  }, [team, onAction, router]);

  const availableActions = useMemo(() => {
    if (!team || !canManageTeam) return [];

    const actions = [];

    actions.push({
      type: 'view',
      component: (
        <MenuItem
          onClick={() => router.push(`/team/${team.id}`)}
          icon={Eye}
          label="View Details"
        />
      ),
    });

    const tournamentStatus = tournament?.status || team.tournament?.status;

    if (tournamentStatus === 'DRAFT' || tournamentStatus === 'REGISTRATION') {
      actions.push({
        type: 'edit',
        component: (
          <MenuItem
            onClick={() => onEdit?.(team)}
            icon={Edit}
            label="Edit Team"
          />
        ),
      });

      actions.push({
        type: 'delete',
        component: (
          <MenuItem
            onClick={handleDelete}
            icon={Trash}
            label="Delete Team"
            className="text-red-700"
          />
        ),
      });
    }

    return actions;
  }, [team, canManageTeam, tournament, onEdit, handleDelete, router]);

  if (!team || !canManageTeam) return null;

  const primaryActions = availableActions.filter((a) => a.type !== 'delete');
  const destructiveActions = availableActions.filter(
    (a) => a.type === 'delete'
  );

  return (
    <div className="relative inline-block">
      <Popover>
        {({ open, close }) => (
          <>
            <PopoverButton
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'inline-flex items-center justify-center rounded-lg bg-black/50 p-2 text-white shadow-sm backdrop-blur-sm transition-all duration-200',
                'hover:bg-black/60',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                'z-10'
              )}
            >
              <MoreVertical className="h-4 w-4" />
            </PopoverButton>

            <Transition
              as={Fragment}
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <PopoverPanel className="absolute right-0 top-full z-50 mt-2 w-56 origin-top-right rounded-lg bg-white/90 shadow-xl ring-1 ring-black/5 backdrop-blur-md focus:outline-none">
                <div className="py-1">
                  {primaryActions.length > 0 && (
                    <div className="border-b border-gray-100 py-1">
                      {primaryActions.map((a, i) => (
                        <div
                          key={i}
                          onClick={() => {
                            a.component.props.onClick();
                            close();
                          }}
                        >
                          {a.component}
                        </div>
                      ))}
                    </div>
                  )}

                  {destructiveActions.length > 0 && (
                    <div className="py-1">
                      {destructiveActions.map((a, i) => (
                        <div
                          key={i}
                          onClick={() => {
                            a.component.props.onClick();
                            close();
                          }}
                        >
                          {a.component}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
}
